import React, { createContext, useState, useContext, useEffect } from "react";

const ShareDataContext = createContext();

// Security: Define allowed fields and their validators
const SHARE_DATA_SCHEMA = {
  id: (value) => {
    const num = Number(value);
    return Number.isInteger(num) && num > 0;
  },
  email: (value) => {
    return typeof value === 'string' && 
           value.length > 0 && 
           value.length <= 255 &&
           /^[^\x00-\x1F\x7F<>"'`]*$/.test(value); // Reject control chars and XSS-prone chars
  },
  name: (value) => {
    return typeof value === 'string' && 
           value.length > 0 && 
           value.length <= 255 &&
           /^[^\x00-\x1F\x7F<>"'`]*$/.test(value); // Reject control chars and XSS-prone chars
  },
  token: (value) => {
    return typeof value === 'string' && 
           value.length > 0 && 
           value.length <= 500 &&
           /^[a-zA-Z0-9_-]*$/.test(value); // Only allow alphanumeric, underscore, hyphen
  }
};

const MAX_DECODED_SIZE = 10000; // 10KB limit for decoded data

/**
 * Validates and sanitizes parsed share data
 * @param {*} data - The parsed JSON data
 * @returns {Object|null} - Validated data or null if invalid
 */
function validateShareData(data) {
  // Must be a plain object
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    console.error('Share data must be a plain object');
    return null;
  }

  // Check for prototype pollution attempts (only check own properties)
  if (Object.prototype.hasOwnProperty.call(data, '__proto__') || 
      Object.prototype.hasOwnProperty.call(data, 'constructor') || 
      Object.prototype.hasOwnProperty.call(data, 'prototype')) {
    console.error('Share data contains forbidden properties');
    return null;
  }

  // Validate that 'id' field is present and valid (required field)
  if (!('id' in data) || !SHARE_DATA_SCHEMA.id(data.id)) {
    console.error('Share data missing valid "id" field');
    return null;
  }

  // Create sanitized object with only allowed fields
  const sanitized = {};
  
  for (const [key, value] of Object.entries(data)) {
    // Skip fields not in schema
    if (!(key in SHARE_DATA_SCHEMA)) {
      console.warn(`Ignoring unexpected field in share data: ${key}`);
      continue;
    }

    // Validate field
    if (!SHARE_DATA_SCHEMA[key](value)) {
      console.error(`Invalid value for field "${key}" in share data`);
      return null;
    }

    // Add validated field (convert id to number)
    sanitized[key] = key === 'id' ? Number(value) : value;
  }

  return sanitized;
}

export function useShareData() {
  return useContext(ShareDataContext);
}

export function ShareDataProvider({ children }) {
  const [shareData, setShareData] = useState(null);

  useEffect(() => {
    // Parse share data from URL parameters on app load
    const urlParams = new URLSearchParams(window.location.search);
    const dataParam = urlParams.get("data");

    if (dataParam) {
      try {
        // Security: Check encoded data size to prevent DoS
        if (dataParam.length > MAX_DECODED_SIZE * 2) {
          console.error("Share data parameter exceeds maximum allowed size");
          setShareData(null);
          return;
        }

        // URL-decode first, then decode base64
        const decodedData = atob(decodeURIComponent(dataParam));
        
        // Security: Check decoded size
        if (decodedData.length > MAX_DECODED_SIZE) {
          console.error("Decoded share data exceeds maximum allowed size");
          setShareData(null);
          return;
        }

        // Parse JSON with reviver to prevent prototype pollution
        const parsedData = JSON.parse(decodedData, (key, value) => {
          // Reject __proto__, constructor, and prototype keys
          if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
            return undefined;
          }
          return value;
        });
        
        // Security: Validate and sanitize the parsed data
        const validatedData = validateShareData(parsedData);
        
        if (validatedData) {
          setShareData(validatedData);
        } else {
          console.error("Share data validation failed");
          setShareData(null);
        }
      } catch (error) {
        console.error("Failed to parse share data from URL:", error);
        setShareData(null);
      }
    } else {
      setShareData(null);
    }
  }, []);

  const value = {
    shareData,
    setShareData,
  };

  return (
    <ShareDataContext.Provider value={value}>
      {children}
    </ShareDataContext.Provider>
  );
}
